import { isDefined } from "@omni/utils/helper.utils";
import { cn } from "@omni/utils/styles";
import { AxiosResponse } from "axios";
import { isEmpty } from "lodash-es";
import { ComponentType } from "react";

import { useBFFStockService } from "@/api/hooks/stockService.hook";
import { useAvailableInStore } from "@/components/Search/SearchResults/hooks/useAvailableInStore/useAvailableInStore.hook";
import { useSearchComponentStateSelector } from "@/redux/modules/value/search/search.selectors";
import { AlgoliaSearchHit } from "@/services/algolia/types/search.types";
import { ProductAlgoliaModel } from "@/types/service/product/algolia/product.algolia.model";
import { RealTimeStockModel } from "@/types/service/stock/realtimeStock.model";
import { ResultListHit } from "@/utils/search/types/internal/resultListHit";
import { SearchComponent } from "@/utils/search/types/searchComponent";

import { isHit } from "../../../../../resultList/helpers/resultListHitType.helpers";
import {
    ResultList,
    ResultListHitComponentProps
} from "../../../../../resultList/resultList.container";

const productStock = new Map<string, number>();

type Props = {
    className?: string;
    hitComponent: ComponentType<
        ResultListHitComponentProps<ProductAlgoliaModel>
    >;
};

const Base = ({ className, hitComponent }: Props) => {
    const { preferredStore, selected } = useAvailableInStore();

    const stockServiceApi = useBFFStockService();

    const getRealtimeStock = (params: { skus: string; storeId: string }) => {
        return stockServiceApi.get<
            {},
            AxiosResponse<Array<RealTimeStockModel>>
        >("/realtime", {
            params: params
        });
    };

    const resultListComponentState = useSearchComponentStateSelector(
        SearchComponent.PRODUCT_RESULT_LIST
    );

    const hits = resultListComponentState?.hits as
        | Array<ResultListHit<AlgoliaSearchHit<ProductAlgoliaModel>>>
        | undefined;

    const getEnhancedHits = () => {
        if (!hits?.length) return;
        if (!selected) return;
        if (!isDefined(preferredStore)) return;

        const productHits = hits
            .filter(isHit)
            .filter((hit) => !productStock.has(hit.sku));

        const skus = productHits.map(({ sku }) => sku);

        const stockPromise = isEmpty(skus)
            ? new Promise<Map<string, number>>((resolve) =>
                  resolve(productStock)
              )
            : (async () => {
                  const { data } = await getRealtimeStock({
                      skus: skus.join(","),
                      storeId: preferredStore.code
                  });

                  data.forEach((productStockEntry) => {
                      productStock.set(
                          productStockEntry.sku,
                          productStockEntry.amount
                      );
                  });

                  return productStock;
              })();

        return hits.map((hit) =>
            isHit(hit)
                ? {
                      ...hit,
                      realtimeStockPromise: stockPromise.then(
                          (productStock) => productStock.get(hit.sku) ?? null
                      )
                  }
                : hit
        );
    };

    const effectiveHits = getEnhancedHits() ?? hits;

    if (!effectiveHits?.length) return null;

    return (
        <ResultList
            className={cn(className)}
            component={SearchComponent.PRODUCT_RESULT_LIST}
            hits={effectiveHits}
            keyExtractor={(hit) => hit.sku}
            HitComponent={hitComponent}
        />
    );
};

export { Base };
